import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { B, FONT } from '../../lib/constants';
import { uploadToDrive } from '../../lib/google';

const CATEGORIES = [
  'Foundation', 'Exterior', 'Interior', 'Concrete', 'Windows/Doors',
  'Plumbing', 'Electrical', 'HVAC', 'Tile', 'Flooring', 'Paint/Stain', 'Finishes',
];

const DECISION_PHOTO_FOLDERS = {
  1: '156CZCv45M_MRtgCMJX_Vq5DcGJJd6zuX',
  2: '1WbL75O52-ea50nLiwJuNUPjVq-s5Ufn6',
  3: '1CFJOP_rfY47wEEXlvPjq8S7opiYWAS-x',
};

export default function AddDecisionModal({ projects, defaultProjectId, onClose, onSuccess, isClient = false, createdBy = 'builder' }) {
  const [projectId, setProjectId] = useState(defaultProjectId || projects?.[0]?.id || '');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [answerType, setAnswerType] = useState('text'); // clients always get text
  const [options, setOptions] = useState(['', '']);
  const [costList, setCostList] = useState(['', '']);
  const [comments, setComments] = useState('');
  const defaultDue = new Date(); defaultDue.setDate(defaultDue.getDate() + 14);
  const [dueDate, setDueDate] = useState((() => { const d = defaultDue; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [internalNotes, setInternalNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef();
  const dropRef = useRef();

  const addOption = () => { if (options.length < 5) { setOptions([...options, '']); setCostList([...costList, '']); } };
  const removeOption = (i) => { if (options.length > 2) { setOptions(options.filter((_, idx) => idx !== i)); setCostList(costList.filter((_, idx) => idx !== i)); } };
  const updateOption = (i, val) => setOptions(options.map((o, idx) => idx === i ? val : o));

  const handlePhotoFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) { handlePhotoFile(file); break; }
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handlePhotoFile(file);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);

    let photoUrl = null, photoDriveId = null;

    // Upload photo if attached
    if (photoFile) {
      const token = localStorage.getItem('google_access_token');
      const folderId = DECISION_PHOTO_FOLDERS[parseInt(projectId)];
      if (token && folderId) {
        try {
          const uploaded = await uploadToDrive(photoFile, folderId, `decision_${Date.now()}.${photoFile.name?.split('.').pop() || 'jpg'}`);
          photoUrl = uploaded.webViewLink;
          photoDriveId = uploaded.id;
        } catch(e) { console.error('Photo upload error:', e); }
      }
    }

    const validOptions = options.filter(o => o.trim());
    const optionCosts = (() => {
      const m = {};
      options.forEach((o, i) => {
        const v = parseFloat((costList[i] || '').toString().replace(/[^0-9.]/g, ''));
        if (o.trim() && !isNaN(v) && v > 0) m[o.trim()] = v;
      });
      return Object.keys(m).length ? m : null;
    })();
    // Always proceed to save even if photo upload fails
    const { error } = await supabase.from('decisions').insert({
      project_id: parseInt(projectId),
      item: name.trim(),
      category: category || null,
      answer_type: isClient ? 'text' : answerType,
      options: (!isClient && answerType === 'options') ? validOptions : null,
      option_costs: (!isClient && answerType === 'options') ? optionCosts : null,
      notes: comments.trim() || null,
      internal_notes: (!isClient && internalNotes.trim()) ? internalNotes.trim() : null,
      due_date: dueDate || null,
      status: 'pending',
      created_by: createdBy,
      photo_url: photoUrl,
      photo_drive_id: photoDriveId,
    });

    setSaving(false);
    if (error) { console.error('Save decision error:', error); alert('Error saving: ' + error.message); return; }
    if (onSuccess) onSuccess();
    onClose();
  };

  const inputStyle = {
    width: '100%', background: B.mid, border: `1px solid ${B.slate}`,
    borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13,
    outline: 'none', fontFamily: FONT, boxSizing: 'border-box',
  };
  const labelStyle = { color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6, display: 'block' };
  const canSave = name.trim() && (isClient || answerType === 'text' || options.filter(o => o.trim()).length >= 2);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: B.navy, borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ color: B.goldlt, fontSize: 17, fontWeight: 800 }}>Add Decision</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Project — builder only */}
        {!isClient && projects?.length > 1 && (
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Project</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} style={inputStyle}>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {/* Decision Name */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Decision Name *</label>
          <input autoFocus placeholder="e.g. Master bath tile selection" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
        </div>

        {/* Category */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Category</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(category === c ? '' : c)}
                style={{ background: category === c ? B.goldlt : B.mid, color: category === c ? B.black : B.light, border: `1px solid ${category === c ? B.goldlt : B.slate}`, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: category === c ? 700 : 400, fontFamily: FONT }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Answer Type — builder only */}
        {!isClient && (
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Answer Type *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[{ val: 'options', label: '📋 Options', sub: 'List 2–5 choices' }, { val: 'text', label: '✏️ Text Box', sub: 'Free-form answer' }].map(t => (
                <button key={t.val} onClick={() => setAnswerType(t.val)}
                  style={{ background: answerType === t.val ? 'rgba(154,115,47,0.2)' : B.mid, border: `2px solid ${answerType === t.val ? B.goldlt : B.slate}`, borderRadius: 12, padding: '14px 12px', cursor: 'pointer', fontFamily: FONT, textAlign: 'center' }}>
                  <div style={{ color: answerType === t.val ? B.goldlt : B.white, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{t.label}</div>
                  <div style={{ color: B.muted, fontSize: 11 }}>{t.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Options — builder only */}
        {!isClient && answerType === 'options' && (
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Options (2–5)</label>
            {options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input placeholder={`Option ${i + 1}`} value={opt} onChange={e => updateOption(i, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="$ approx" value={costList[i] || ''} inputMode="decimal"
                  onChange={e => setCostList(costList.map((x, xi) => xi === i ? e.target.value : x))}
                  style={{ ...inputStyle, width: 90, flex: 'none', color: B.goldlt, fontSize: 12 }} />
                {options.length > 2 && (
                  <button onClick={() => removeOption(i)} style={{ background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '0 12px', cursor: 'pointer', color: B.muted, fontSize: 16 }}>×</button>
                )}
              </div>
            ))}
            {options.length < 5 && (
              <button onClick={addOption} style={{ background: 'transparent', border: `1px dashed ${B.slate}`, borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: B.muted, fontSize: 12, fontFamily: FONT, width: '100%' }}>+ Add Option</button>
            )}
          </div>
        )}

        {/* Comments — with paste support */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Comments {isClient && <span style={{ color: B.muted, fontSize: 9, letterSpacing: 0 }}>(you can paste a photo here)</span>}</label>
          <textarea
            placeholder={isClient ? "Describe what you want… or paste a photo" : "Notes, context, pricing info…"}
            value={comments}
            onChange={e => setComments(e.target.value)}
            onPaste={handlePaste}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>

        {/* Photo upload — drag & drop */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Photo <span style={{ color: B.muted, fontSize: 9, letterSpacing: 0 }}>(optional — drag, paste, or tap)</span></label>
          <div
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => photoRef.current?.click()}
            style={{ background: B.mid, border: `2px dashed ${photoPreview ? B.goldlt : B.slate}`, borderRadius: 12, padding: '16px', cursor: 'pointer', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            {photoPreview ? (
              <div style={{ position: 'relative' }}>
                <img src={photoPreview} alt="" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, display: 'block', margin: '0 auto' }} />
                <button onClick={e => { e.stopPropagation(); setPhotoFile(null); setPhotoPreview(null); }}
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: '#fff', fontSize: 14, lineHeight: '24px', textAlign: 'center', padding: 0 }}>×</button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
                <div style={{ color: B.muted, fontSize: 12, fontFamily: FONT }}>Drag a photo here, paste, or tap to select</div>
              </div>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" onChange={e => handlePhotoFile(e.target.files?.[0])} style={{ display: 'none' }} />
        </div>

        {/* Internal Notes — builder only, clients never see */}
        {!isClient && (
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Internal Notes <span style={{ color: B.muted, fontSize: 9, letterSpacing: 0 }}>(only you see this)</span></label>
            <textarea placeholder="Your private notes about this decision…" value={internalNotes} onChange={e => setInternalNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
          </div>
        )}

        {/* Date — builder only */}
        {!isClient && (
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Date Needed</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
          </div>
        )}

        <button onClick={handleSave} disabled={!canSave || saving}
          style={{ width: '100%', background: canSave ? B.goldlt : B.slate, color: canSave ? B.black : B.muted, border: 'none', borderRadius: 12, padding: 15, cursor: canSave ? 'pointer' : 'default', fontSize: 14, fontWeight: 800, fontFamily: FONT, marginTop: !isClient ? 0 : 6 }}>
          {saving ? 'Saving…' : 'Save Decision'}
        </button>
      </div>
    </div>
  );
}
